import { useState } from 'react';
import { Heart, Lock, Unlock, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

export const SecretPage = () => {
  const [password, setPassword] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [error, setError] = useState('');
  const [isShaking, setIsShaking] = useState(false);

  const correctPassword = 'Bokkalief@918';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === correctPassword) {
      setIsUnlocked(true);
      setError('');
    } else {
      setError('Incorrect password. Try again, my love.');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
    }
  };

  if (isUnlocked) {
    return (
      <div className="min-h-screen pt-20 bg-[#0F0505] flex items-center justify-center" data-testid="secret-page-unlocked">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-20">
          {/* Unlocked Content */}
          <div className="text-center animate-fade-in">
            {/* Celebration Icon */}
            <div className="relative inline-block mb-8">
              <Sparkles className="w-16 h-16 text-[#D4AF37]" />
              <div className="absolute inset-0 animate-ping">
                <Sparkles className="w-16 h-16 text-[#D4AF37] opacity-20" />
              </div>
            </div>

            {/* Title */}
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#F5F5F0] mb-6">
              You Found It
            </h1>

            {/* Decorative Line */}
            <div className="w-24 h-0.5 bg-[#D4AF37] mx-auto mb-8" />

            {/* Secret Message */}
            <div className="glass rounded-2xl p-8 md:p-12 mb-12">
              <Heart className="w-10 h-10 text-[#D4AF37] mx-auto mb-6" fill="#D4AF37" />
              
              <p className="font-['Cormorant_Garamond'] text-2xl md:text-3xl text-[#F5F5F0] italic leading-relaxed mb-8">
                If you're reading this, it means you found the secret page...
              </p>

              <div className="space-y-6 text-[#A18E90] text-lg leading-relaxed text-left">
                <p>
                  My darling wife,
                </p>
                <p>
                  One year ago, I stood across from you and promised you forever. 
                  Today, I want you to know that every single day since then has only 
                  made me more certain that I made the right choice.
                </p>
                <p>
                  You are my best friend, my partner in everything, and the love of my life. 
                  You make me laugh when I want to cry, you hold my hand when I'm scared, 
                  and you believe in me even when I don't believe in myself.
                </p>
                <p>
                  This year has been filled with incredible adventures — from our honeymoon 
                  to exploring new cities together. But my favorite moments are the quiet ones. 
                  Morning coffee together. Late night conversations. Your head on my shoulder 
                  while we watch our favorite shows.
                </p>
                <p>
                  I built this website as a small way to capture what this year has meant to me. 
                  But no website, no words, could ever fully express how much I love you.
                </p>
                <p className="text-[#D4AF37] font-['Playfair_Display'] text-xl">
                  Here's to year two, and every year after that. Forever and always.
                </p>
                <p className="text-[#F5F5F0] text-right font-['Cormorant_Garamond'] text-2xl italic mt-8">
                  Your husband
                </p>
              </div>
            </div>

            {/* Final Message */}
            <div className="text-center">
              <p className="text-[#D4AF37] text-lg tracking-widest uppercase">
                Happy First Anniversary
              </p>
              <Heart className="w-8 h-8 text-[#D4AF37] mx-auto mt-4" fill="#D4AF37" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-[#0F0505] flex items-center justify-center" data-testid="secret-page-locked">
      <div className="max-w-md mx-auto px-4 md:px-8 py-20 text-center">
        {/* Locked State */}
        <div className={`animate-fade-in ${isShaking ? 'animate-shake' : ''}`}>
          {/* Lock Icon */}
          <div className="relative inline-block mb-8 secret-lock">
            <div className="w-24 h-24 rounded-full bg-[#1A0F0F] border-2 border-[#D4AF37]/50 flex items-center justify-center">
              <Lock className="w-12 h-12 text-[#D4AF37]" />
            </div>
          </div>

          {/* Title */}
          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#F5F5F0] mb-4">
            A Secret Awaits
          </h1>

          <p className="text-[#A18E90] mb-8">
            This page holds a special message, just for you.
          </p>

          {/* Password Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="secret-password-form">
            <div className="relative">
              <Input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                placeholder="Enter the password..."
                className="bg-transparent border-[#D4AF37]/30 focus:border-[#D4AF37] text-[#F5F5F0] text-center tracking-widest placeholder:text-[#A18E90]/50 py-6"
                data-testid="secret-password-input"
              />
            </div>

            {error && (
              <p className="text-[#722F37] text-sm animate-fade-in" data-testid="password-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              className="w-full bg-[#D4AF37] hover:bg-[#F5F5F0] text-[#0F0505] font-semibold py-6 rounded-full transition-all duration-300"
              data-testid="unlock-btn"
            >
              <Unlock className="w-5 h-5 mr-2" />
              Unlock
            </Button>
          </form>

          {/* Hint */}
          <p className="text-[#A18E90]/50 text-sm mt-8">
            Hint: Our special word + our number
          </p>
        </div>
      </div>

      {/* Custom shake animation */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};
